import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { photoFillImageTransform } from '@/lib/photos/photo-fill-transform'

/** Opacity of the photo-fill ghost overlay outside the shape's clip path. */
const PHOTO_FILL_GHOST_OPACITY = 0.3

interface PhotoFillGhostProps {
  photoFill: ShapePhotoFill
  /** Live shape geometry (doc space). */
  ox: number
  oy: number
  width: number
  height: number
}

/**
 * Photo-fill ghost: full uncropped photo at reduced opacity, visual reference only
 * (not interactive). Rendered first (bottom of paint order) so anchors/handles
 * stay on top. Unlike shape-renderer's shape-local viewBox, this overlay's SVG is
 * canvas-absolute, so an outer translate places the image's local (0,0) frame at
 * the shape's live x/y before the shared pan/zoom transform is applied.
 */
export function PhotoFillGhost({ photoFill, ox, oy, width, height }: PhotoFillGhostProps) {
  return (
    <g transform={`translate(${ox} ${oy})`}>
      <image
        href={photoFill.previewUrl}
        x={0}
        y={0}
        width={width}
        height={height}
        preserveAspectRatio="xMidYMid slice"
        opacity={PHOTO_FILL_GHOST_OPACITY}
        transform={photoFillImageTransform({
          frameWidth: width,
          frameHeight: height,
          offsetX: photoFill.offsetX,
          offsetY: photoFill.offsetY,
          scale: photoFill.scale,
          flippedX: photoFill.flippedX,
          flippedY: photoFill.flippedY,
          rotation: photoFill.rotation,
        })}
        pointerEvents="none"
      />
    </g>
  )
}
